/**
 * `/roles` — the roles.
 *
 * Roles are **data**; permissions are **code**. You can make as many roles as you
 * like, but only out of the permissions this build defines — which is why the
 * editor renders its checkboxes from `GET /api/permissions` rather than from a
 * list typed out here that would drift the first time the backend adds one.
 *
 * Built-in roles are marked. Their permissions can still be edited; the flag
 * says the role came with the system, not that it is frozen.
 *
 * A row carries **both** ways in: the name links to the permission editor, the
 * pencil renames, the bin deletes. That is the rule in `../README.md` under "How a row opens",
 * and the rename and delete are declared once in
 * [`../entities/roleActions.tsx`](../entities/roleActions.tsx) so this screen and
 * `RoleDetailPage` cannot drift apart about what either looks like.
 */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { RoleDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { FormDialog } from '../../components/FormDialog';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { useCrudDialogs } from '../../hooks/useCrudDialogs';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { useAuth } from '../../auth/useAuth';
import { createRole, listRoles } from '../../api/identity';
import {
  BLANK_ROLE_FORM,
  RoleFormNote,
  useRoleActions,
  useRoleFields,
  type RoleForm,
} from '../entities/roleActions';

export default function RolesPage() {
  const { t } = useTranslation(['people', 'common']);
  const { can } = useAuth();
  const list = useListQuery({ defaultSort: 'name' });
  const roles = useAsync(() => listRoles(list.query), [list.key]);

  // Everything that can be done to a role, declared once in the entity file and
  // rendered here as row icons and on `RoleDetailPage` as header buttons.
  const actions = useRoleActions({
    onChanged: () => roles.reload(),
    onDeleted: () => roles.reload(),
  });
  const roleFields = useRoleFields();

  // Creating is this screen's own act, not the entity's edit: a new role is
  // made here and nowhere else. It reuses the editor's fields, so the two
  // dialogs cannot disagree about what a role's name and description look like.
  // `formFor` is inert here — this dialog only ever creates, so `openEdit` is
  // never called on it. Renaming a role goes through `actions` above.
  const crud = useCrudDialogs<RoleDto, RoleForm>({
    blank: BLANK_ROLE_FORM,
    formFor: () => BLANK_ROLE_FORM,
  });
  const { form } = crud;

  const create = useSubmit(async () => {
    // Created empty, then filled in on the detail screen. Two steps rather than
    // a dialog carrying thirty checkboxes — and it keeps the "what does this
    // role do" question in the one place that answers it well.
    const created = await createRole({
      name: form.name.trim(),
      permissions: [],
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
    });
    roles.reload();
    return created;
  });

  return (
    <>
      <PageHeader
        title={t('people:roles.title')}
        description={t('people:roles.description')}
        actions={
          can('role.create') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => crud.openCreate()}>
              {t('people:roles.new')}
            </Button>
          )
        }
      />

      <AsyncContent state={roles}>
        {({ roles: rows, page }) => (
          <EntityTable
            rows={rows}
            rowKey={(role) => role.id}
            list={list}
            page={page}
            empty={{
              icon: <BadgeIcon fontSize="inherit" />,
              title: t('people:roles.emptyTitle'),
              description: t('people:roles.emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('people:roles.column.name'),
                sortable: true,
                render: (role) => (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Link
                      component={RouterLink}
                      to={`/roles/${role.id}`}
                      underline="hover"
                      color="inherit"
                      sx={{ fontWeight: 500 }}
                    >
                      {role.name}
                    </Link>
                    {role.isBuiltin && (
                      <Chip size="small" variant="outlined" label={t('people:roles.builtin')} />
                    )}
                  </Stack>
                ),
              },
              // Description and the permission count are not sortable: one is
              // free text nobody scans by, the other is a count the server does
              // not store as a column.
              {
                field: 'description',
                label: t('people:roles.column.description'),
                render: (role) => (
                  <Typography variant="body2" color="text.secondary">
                    {role.description ?? t('common:state.empty')}
                  </Typography>
                ),
              },
              {
                field: 'permissions',
                label: t('people:roles.column.permissions'),
                width: 160,
                render: (role) => (
                  <Chip
                    size="small"
                    label={t('people:roles.permissionCount', { count: role.permissions.length })}
                  />
                ),
              },
            ]}
            actionsWidth={56}
            actions={actions.rowActions}
          />
        )}
      </AsyncContent>

      {/* The row actions' dialogs. A table renders rows, not dialogs, so this
          line is the one thing the declaration cannot mount for a list screen —
          and an icon whose dialog was never mounted does nothing. */}
      {actions.dialogs}

      <FormDialog
        open={crud.isOpen}
        title={t('people:roles.createDialog.title')}
        submitLabel={t('people:roles.createDialog.submit')}
        pending={create.pending}
        error={create.error}
        disabled={form.name.trim() === ''}
        onClose={crud.close}
        onSubmit={async () => {
          if (await create.submit()) crud.close();
        }}
      >
        {roleFields(form, crud.patch)}
        <RoleFormNote mode="create" />
      </FormDialog>
    </>
  );
}
